import { useRouter } from 'next/router'
import Head from 'next/head'
import { groq } from 'next-sanity'
import { getClient } from '@lib/sanity'
import { Trans, t } from '@lingui/macro'
import { FaExternalLinkSquareAlt, FaEnvelope } from 'react-icons/fa'
import { loadTranslation } from '@lib/i18n'
import Header from 'components/ui/Header'
import H1 from 'components/ui/H1'
import H2 from 'components/ui/H2'
import Content from 'components/Content'
import ButtonLink from 'components/ButtonLink'
import { formatDate } from '@lib/i18n'

const Jobs = ({ items }) => {
  const { locale } = useRouter()

  return (
    <div className="container max-w-3xl pb-10">
      <Head>
        <title key="title">
          {`${t`Jobs Board`} - ${process.env.NEXT_PUBLIC_SITE_TITLE}`}
        </title>
        <meta property="og:title" content={t`Jobs Board`} key="og:title" />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_DOMAIN}${
            locale == 'en' ? `` : `/${locale}`
          }/jobs`}
          key="canonical"
        />
        <meta
          property="og:url"
          content={`${process.env.NEXT_PUBLIC_DOMAIN}${
            locale == 'en' ? `` : `/${locale}`
          }/jobs`}
          key="og:url"
        />
      </Head>
      <Header>
        <H1>
          <Trans>Jobs Board</Trans>
        </H1>
      </Header>
      {items && !!items.length ? (
        <div>
          <ul>
            {items.map((item) => (
              <li
                key={item._id}
                className="mb-4 rounded-lg border border-primary-100 bg-white px-6 pt-6 pb-4 shadow-sm"
              >
                <H2>{item.title}</H2>
                <p className="mb-2 uppercase text-primary-300">
                  {item.organisation && <span>&bull; {item.organisation}</span>}{' '}
                  {item.location && <span>&bull; {item.location}</span>}
                </p>
                {item.body && <Content content={item.body} variant="base" />}
                <div className="flex-row items-center justify-between md:flex">
                  {item.deadline && (
                    <div className="mt-2">
                      <Trans>Deadline:</Trans>{' '}
                      {formatDate(
                        new Date(`${item.deadline}T00:00:00Z`),
                        'd MMMM yyyy',
                        locale
                      )}
                    </div>
                  )}
                  {item.link && (
                    <div className="mt-2">
                      <ButtonLink
                        href={item.link}
                        target="_blank"
                        variant="secondary"
                      >
                        <FaExternalLinkSquareAlt className="mr-2" />
                        <Trans>More info</Trans>
                      </ButtonLink>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div>
            <p className="mt-10 md:text-xl">
              <Trans>
                If you are an organisation with a job that you would like us to
                promote, please get in touch.
              </Trans>
            </p>
            <div className="mt-4 flex flex-row justify-center">
              <ButtonLink href="/[[...slug]]" as="/contact">
                <FaEnvelope className="mr-2" />
                <Trans>Contact us</Trans>
              </ButtonLink>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-4 rounded-lg border border-primary-100 bg-white px-6 pt-6 pb-4 shadow-sm">
          <p className="mb-4 text-xl font-medium">
            <Trans>
              Sorry, there are no jobs currently posted to the IDPC jobs board.
            </Trans>
          </p>
          <p>
            <Trans>
              If you are an organisation with a job that you would like us to
              promote, please get in touch.
            </Trans>
          </p>
          <div className="mt-4 flex flex-row justify-center">
            <ButtonLink href="/[[...slug]]" as="/contact">
              <FaEnvelope className="mr-2" />
              <Trans>Contact us</Trans>
            </ButtonLink>
          </div>
        </div>
      )}
    </div>
  )
}

const jobsQuery = groq`*[_type == "job" && active && !defined(deadline) || dateTime(deadline + 'T00:00:00Z') >= dateTime(now())] | order(deadline asc, title asc){_id, title, organisation, body, link, location, deadline, active}`

export const getStaticProps = async ({ locale, params, preview = false }) => {
  const translation = await loadTranslation(
    locale,
    process.env.NODE_ENV === 'production'
  )
  const items = await getClient(preview).fetch(jobsQuery, {
    locale,
  })

  return {
    props: {
      translation,
      items,
      preview,
    },
    revalidate: 60 * 5, // Every five mins
  }
}

export default Jobs
